import { Box, BoxProps, FormikTextInput } from '@palmetto/palmetto-components';
import { Field } from 'formik';

import {
  GeothermalEquipmentTypeLabels,
  GeothermalIndoorUnitTypeLabels,
  GeothermalLoopTypeLabels,
  GeothermalSystem,
} from 'types';

import { matchStoredEquipment } from '../../DesignDocuments/loopFieldSystemDetails';

const EMPTY_VALUE = '—';

const fieldRowDirection: BoxProps['direction'] = { base: 'column', tablet: 'row' };

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Box direction="row" borderColor="separator" borderWidth="0 0 xs 0" padding="xs 0">
    <Box width="40" fontWeight="medium">
      {label}
    </Box>
    <Box width="60">{value}</Box>
  </Box>
);

const describeAuxiliaryHeat = (system: GeothermalSystem) => {
  if (!system.auxiliaryHeat) return 'No';
  return system.auxiliaryHeatType ? `Yes — ${system.auxiliaryHeatType}` : 'Yes';
};

interface SystemCardProps {
  system: GeothermalSystem;
  index: number;
  isDisabled: boolean;
}

const SystemCard = ({ system, index, isDisabled }: SystemCardProps) => {
  const requiredEquipment = matchStoredEquipment(system);
  const showTypeLabel = requiredEquipment.length > 1;

  return (
    <Box padding="lg" background="secondary" radius="md" gap="lg">
      <Box fontWeight="medium">{system.name || `System ${index + 1}`}</Box>
      <Box fontSize="sm">
        <SummaryRow label="Manufacturer" value={system.manufacturer || EMPTY_VALUE} />
        <SummaryRow
          label="Loop Type"
          value={(system.loopType && GeothermalLoopTypeLabels[system.loopType]) || EMPTY_VALUE}
        />
        <SummaryRow
          label="Indoor Unit Type"
          value={(system.indoorUnitType && GeothermalIndoorUnitTypeLabels[system.indoorUnitType]) || EMPTY_VALUE}
        />
        <SummaryRow label="Capacity" value={system.capacityTons ? `${system.capacityTons} tons` : EMPTY_VALUE} />
        <SummaryRow label="DHW Assist" value={system.dhwAssist ? 'Yes' : 'No'} />
        <SummaryRow label="Auxiliary Heat" value={describeAuxiliaryHeat(system)} />
      </Box>
      {requiredEquipment.length > 0 && (
        <Box gap="md">
          <Box fontSize="sm" fontWeight="medium">
            Equipment
          </Box>
          {requiredEquipment.map(({ type }, equipmentIndex) => (
            <Box key={type} gap="md">
              {showTypeLabel && (
                <Box fontSize="sm" fontWeight="medium" color="body-secondary">
                  {GeothermalEquipmentTypeLabels[type]}
                </Box>
              )}
              <Box direction={fieldRowDirection} gap="lg">
                <Box flex="auto" minWidth="0">
                  <Field
                    label="Model"
                    name={`systems.${index}.equipment.${equipmentIndex}.model`}
                    id={`systems.${index}.equipment.${equipmentIndex}.model`}
                    component={FormikTextInput}
                    isDisabled={isDisabled}
                  />
                </Box>
                <Box flex="auto" minWidth="0">
                  <Field
                    label="Description (optional)"
                    name={`systems.${index}.equipment.${equipmentIndex}.description`}
                    id={`systems.${index}.equipment.${equipmentIndex}.description`}
                    component={FormikTextInput}
                    isDisabled={isDisabled}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

interface GeothermalSystemDetailsSummaryProps {
  systems: GeothermalSystem[];
  isDisabled: boolean;
}

/**
 * The geothermal design alongside the package, so it can be checked and corrected without navigating
 * away. Configuration is priced into the contract and stays read-only; only equipment model and
 * description are editable. Equipment rows come from `matchStoredEquipment`, the same correlation the
 * Design Documents form uses, and are indexed to match `buildInitialValues`, so the field names here
 * address the values the page seeded.
 */
export const GeothermalSystemDetailsSummary = ({ systems, isDisabled }: GeothermalSystemDetailsSummaryProps) => {
  if (!systems?.length) return null;

  return (
    <Box gap="lg">
      {systems.map((system, index) => (
        // Position is the identity here: field names, `buildInitialValues`, and the server's
        // `mergeSystemDetails` all correlate systems by index, and names are not unique.
        <SystemCard key={index} system={system} index={index} isDisabled={isDisabled} />
      ))}
    </Box>
  );
};
